import React, { useMemo } from 'react';
import { theme } from '../../../../../../tailwind.config';
import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ISalesMetricsByMonth } from 'services/DashboardApi/types';
import { useResponsive } from 'containers/CRM/hooks/useResponsive';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface ITransformedMonthData extends ISalesMetricsByMonth {
  monthLabel: string;
  salesCurrentYear: number;
  salesPreviousYear: number;
}

const formatAxisTick = (value: number): string => {
  if (Math.abs(value) >= 1_000_000) {
    const compactValue = value / 1_000_000;
    return `${Number.isInteger(compactValue) ? compactValue : compactValue.toFixed(1)}M`;
  }

  if (Math.abs(value) >= 1000) {
    const compactValue = value / 1000;
    return `${Number.isInteger(compactValue) ? compactValue : compactValue.toFixed(1)}k`;
  }

  return `${Math.round(value)}`;
};

// Custom tooltip for Sales and Bookings by Month chart
const SalesBookingsTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const formatCurrency = (amount: number) => {
      return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    return (
      <div
        style={{
          backgroundColor: theme.colors['white'],
          border: `1px solid ${theme.colors['gray-40']}`,
          padding: '10px 14px',
          fontFamily: 'PT Sans',
        }}
      >
        <p className="font-pt-sans text-13px font-bold text-black m-0 mb-8px">{label}</p>
        <p className="font-pt-sans text-12px m-0 mb-4px" style={{ color: theme.colors['yellow-50'] }}>
          Sales Current Year: <span style={{ color: theme.colors['black'] }}>{formatCurrency(payload[0]?.value || 0)}</span>
        </p>
        <p className="font-pt-sans text-12px m-0 mb-4px" style={{ color: theme.colors['status-requested'] }}>
          Sales Previous Year: <span style={{ color: theme.colors['black'] }}>{formatCurrency(payload[1]?.value || 0)}</span>
        </p>
        <p className="font-pt-sans text-12px m-0 mb-4px" style={{ color: theme.colors['green-message'] }}>
          Bookings Current Year: <span style={{ color: theme.colors['black'] }}>{payload[2]?.value || 0}</span>
        </p>
        <p className="font-pt-sans text-12px m-0" style={{ color: theme.colors['bright-sky-blue'] }}>
          Bookings Previous Year: <span style={{ color: theme.colors['black'] }}>{payload[3]?.value || 0}</span>
        </p>
      </div>
    );
  }
  return null;
};

interface ISalesBookingsChartProps {
  salesMetricsByMonth: ISalesMetricsByMonth[];
}

export const SalesBookingsChart: React.FC<ISalesBookingsChartProps> = ({ salesMetricsByMonth }) => {
  const { isMobile } = useResponsive();
  // Transform data to include month labels and convert cents to currency
  const chartData = useMemo<ITransformedMonthData[]>(() => {
    return salesMetricsByMonth.map(item => ({
      ...item,
      monthLabel: MONTH_LABELS[item.month - 1],
      salesCurrentYear: item.bookingValueCents / 100,
      salesPreviousYear: item.previousYearBookingValueCents / 100,
    }));
  }, [salesMetricsByMonth]);

  // Calculate dynamic Y-axis domains
  const { maxSales, maxBookings } = useMemo(() => {
    const allSales = chartData.flatMap(d => [d.salesCurrentYear, d.salesPreviousYear]);
    const allBookings = chartData.flatMap(d => [d.bookingCount, d.previousYearBookingCount]);

    return {
      maxSales: Math.max(...allSales, 1) * 1.1,
      maxBookings: Math.max(...allBookings, 1) * 1.1,
    };
  }, [chartData]);

  return (
    <div className="bg-white px-20px py-20px rounded">
      <p className="m-0 font-pt-sans text-[13px] leading-[22px] tracking-[0.52px] uppercase text-black flex justify-center">
        Sales and Bookings by Month
      </p>
      <ResponsiveContainer width="100%" height={isMobile ? 320 : 400}>
        <ComposedChart
          data={chartData}
          margin={{
            top: 20,
            right: isMobile ? 6 : 24,
            bottom: 20,
            left: isMobile ? 0 : 8,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={theme.colors['gray-17']} vertical={false} />
          <XAxis
            dataKey="monthLabel"
            tick={{ fill: theme.colors['gray-120'], fontSize: isMobile ? 10 : 12, fontFamily: 'PT Sans' }}
            axisLine={{ stroke: theme.colors['gray-40'] }}
            tickLine={false}
          />
          <YAxis
            yAxisId="left"
            width={isMobile ? 40 : 65}
            label={
              isMobile
                ? undefined
                : {
                    value: 'Sales ($)',
                    angle: -90,
                    position: 'insideLeft',
                    dx: 8,
                    style: { fontSize: 12, fontFamily: 'PT Sans', fill: theme.colors['gray-100'] },
                  }
            }
            tick={{ fill: theme.colors['gray-120'], fontSize: isMobile ? 10 : 12, fontFamily: 'PT Sans' }}
            axisLine={{ stroke: theme.colors['gray-40'] }}
            tickLine={false}
            allowDecimals={false}
            domain={[0, maxSales]}
            tickFormatter={formatAxisTick}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            width={isMobile ? 32 : 48}
            label={
              isMobile
                ? undefined
                : {
                    value: 'Bookings',
                    angle: 90,
                    position: 'insideRight',
                    style: { fontSize: 12, fontFamily: 'PT Sans', fill: theme.colors['gray-100'] },
                  }
            }
            tick={{ fill: theme.colors['gray-120'], fontSize: isMobile ? 10 : 12, fontFamily: 'PT Sans' }}
            axisLine={{ stroke: theme.colors['gray-40'] }}
            tickLine={false}
            allowDecimals={false}
            domain={[0, maxBookings]}
            tickFormatter={formatAxisTick}
          />
          <Tooltip content={<SalesBookingsTooltip />} />
          <Bar
            yAxisId="left"
            dataKey="salesCurrentYear"
            fill={theme.colors['yellow-50']}
            name="Sales Current Year"
            barSize={isMobile ? 16 : 30}
          />
          <Bar
            yAxisId="left"
            dataKey="salesPreviousYear"
            fill={theme.colors['status-requested']}
            name="Sales Previous Year"
            barSize={isMobile ? 16 : 30}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="bookingCount"
            stroke={theme.colors['green-message']}
            strokeWidth={2}
            name="Bookings Current Year"
            dot={{ fill: theme.colors['green-message'], r: 4, strokeWidth: 0 }}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="previousYearBookingCount"
            stroke={theme.colors['bright-sky-blue']}
            strokeWidth={2}
            name="Bookings Previous Year"
            dot={{ fill: theme.colors['bright-sky-blue'], r: 4, strokeWidth: 0 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
      {/* Custom legend */}
      <div className={`flex items-center mt-10px justify-center flex-wrap ${isMobile ? 'gap-[14px] px-0' : 'gap-30px px-[30px]'}`}>
        <div className="flex items-center gap-8px">
          <div className="w-15px h-15px" style={{ backgroundColor: theme.colors['yellow-50'] }} />
          <p className="font-pt-sans text-13px m-0" style={{ color: theme.colors['black'] }}>
            Sales Current Year
          </p>
        </div>
        <div className="flex items-center gap-8px">
          <div className="w-15px h-15px" style={{ backgroundColor: theme.colors['status-requested'] }} />
          <p className="font-pt-sans text-13px m-0" style={{ color: theme.colors['black'] }}>
            Sales Previous Year
          </p>
        </div>
        <div className="flex items-center gap-8px">
          <div className="w-15px h-15px" style={{ backgroundColor: theme.colors['green-message'] }} />
          <p className="font-pt-sans text-13px m-0" style={{ color: theme.colors['black'] }}>
            Bookings Current Year
          </p>
        </div>
        <div className="flex items-center gap-8px">
          <div className="w-15px h-15px" style={{ backgroundColor: theme.colors['bright-sky-blue'] }} />
          <p className="font-pt-sans text-13px m-0" style={{ color: theme.colors['black'] }}>
            Bookings Previous Year
          </p>
        </div>
      </div>
    </div>
  );
};
